import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type HostMapping = {
    /** How we'll recognize that we are at a URL we want to redirect away from */
    match: string | RegExp;
    /** If a match is made with, use this as the base for the new URL to redirect to */
    targetBaseURL: string;
};

export type UseMigrationRedirectOptions = {
    /** Amount of time, in milliseconds, to wait before redirecting */
    countdownMS?: number;
    /** All the mappings to be checked to know if and where we should redirect */
    mappings: HostMapping[];
};

function hostnameMatches(hostname: string, match: string | RegExp): boolean {
    return typeof match === 'string' ? hostname === match : match?.test(hostname);
}

function buildTargetURL(targetBase: string, from: URL): string | null {
    const target = new URL(targetBase);
    target.pathname = from.pathname;
    // Because we use a hash router, our search query params don't show up in the native
    // `location.search` property since they appear after the hash. Because of this,
    // everything after the hash, INCLUDING OUR QUERY PARAMS, is included within the
    // native `location.hash` property. So, we ARE retaining those query params here.
    target.hash = from.hash;

    return target.toString();
}

/**
 * A hook that helps manage redirecting from one URL to another, based on hostname
 * mappings. It provides a countdown timer and a modal visibility state to inform users
 * about the impending redirect.
 *
 * @example
 * ```tsx
 * const { secondsRemaining, showModal, toURL } = useMigrationRedirect({
 *   countdownMS: 10000,
 *   mappings: [
 *     { match: 'oldsite.com', targetBaseURL: 'https://newsite.com' },
 *     { match: /legacy\.example\.org/, targetBaseURL: 'https://example.org' }
 *   ]
 * });
 *
 * return showModal ? (
 *   <ModalComponent>
 *       <p>You will be redirected in {secondsRemaining} seconds.</p>
 *       <a href={toURL}>Click here if you are not redirected automatically.</a>
 *   </ModalComponent>
 * );
 * ```
 */
export function useMigrationRedirect({
    countdownMS = 15_000,
    mappings
}: UseMigrationRedirectOptions) {
    const [secondsRemaining, setSecondsRemaining] = useState<number>(
        Math.ceil(countdownMS / 1_000)
    );
    const [isShowModal, setShowModal] = useState<boolean>(false);
    const [toURL, setToURL] = useState<string>();

    const fromURL = useMemo(() => new URL(window.location.href), []);
    const mapping = useMemo(
        () => mappings.find(item => hostnameMatches(fromURL.hostname, item.match)),
        [fromURL.hostname, mappings]
    );
    const maybeTargetURL = useMemo(() => {
        if (!mapping) return null;
        return buildTargetURL(mapping?.targetBaseURL, fromURL);
    }, [fromURL, mapping]);

    const timerRef = useRef<number | null>(null);

    const redirect = useCallback(() => {
        // The only way we got here is by there being a valid URL to redirect to. So, we
        // should be ok to assert that `toURL` is defined. If we make this function available
        // to consumers of this hook, we'll then need to validate it here before using it.
        window.location.replace(toURL!);
    }, [toURL]);

    useEffect(() => {
        // We don't recognize the current location as being a location we need to redirect
        // away from. Essentially, we didn't make a match with any of the values passed
        // into `mappings`. If so, don't do anything.
        if (!maybeTargetURL) return;

        setToURL(maybeTargetURL);
        setShowModal(true);

        const deadline = Date.now() + countdownMS;
        const tick = () => {
            const timeRemaining = Math.max(0, deadline - Date.now());
            setSecondsRemaining(Math.ceil(timeRemaining / 1_000));

            if (timeRemaining <= 0) {
                redirect();
            } else {
                timerRef.current = window.setTimeout(tick, Math.min(300, timeRemaining));
            }
        };

        tick();

        // Preconnect and DNS prefetch to the target URL to help speed up the redirect.
        // See: https://developer.mozilla.org/en-US/docs/Web/Performance/Guides/Speculative_loading
        try {
            const url = new URL(maybeTargetURL);
            const preconnect = document.createElement('link');
            preconnect.rel = 'preconnect';
            preconnect.href = url.origin;
            document.head.appendChild(preconnect);

            const dnsPrefetch = document.createElement('link');
            dnsPrefetch.rel = 'dns-prefetch';
            dnsPrefetch.href = url.hostname;
            document.head.appendChild(dnsPrefetch);
        } catch {
            // If the URL is invalid, do nothing...
        }

        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [countdownMS, fromURL.searchParams, maybeTargetURL, redirect]);

    return { secondsRemaining, isShowModal, toURL };
}